import React, { useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";
import ElectricityApartmentAgreementsService, {
  CreateApartmentAgreementRequest,
} from "services/electricity/electricity-apartment-agreements.service";
import CustomerContactPersonsService from "services/customer-contact-persons.service";
import { PERSONAL_CODE_LENGTH } from "common/constants";
import { SortDirection } from "common/interfaces/IPageResponse";
import {
  ICustomerContactPerson,
  ICustomerContactPersonSortFields,
} from "components/CustomerContactPersons/models";
import { getCustomerNotFoundMessage } from "../components/NewElectricityAgreement/NewElectricityAgreementCustomerForm";
import { isEmailValidAndPresent } from "helpers/validator";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import { ReactComponent as ClearIcon } from "../../../assets/icons/close.svg";
import { dateToIso, isoToDate } from "../../../common/dateUtil";

interface Props {
  agreementId: number;
  defaultDateStart?: string;
  parentDateEnd?: string;
  onSaved: () => void;
  onCancel: () => void;
}

interface CustomerSearchState {
  loading: boolean;
  loaded: boolean;
  customerId?: number;
  customerName?: string;
}

const initialForm = {
  name: "",
  dateStart: "",
  dateEnd: "",
  personalIdentificationCode: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  validated: false,
};

type FormField = keyof typeof initialForm;

export default function AddApartmentForm({ agreementId, defaultDateStart, parentDateEnd, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({ ...initialForm, dateStart: defaultDateStart ?? "" });
  const [customer, setCustomer] = useState<CustomerSearchState>({ loading: false, loaded: false });
  const [saving, setSaving] = useState(false);

  const update = (field: FormField, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === "personalIdentificationCode") {
      setCustomer({ loading: false, loaded: false });
    }
  };

  const isSearchDisabled =
    form.personalIdentificationCode.length !== PERSONAL_CODE_LENGTH || customer.loading;

  const onSearch = async () => {
    if (isSearchDisabled) return;
    setCustomer({ loading: true, loaded: false });
    try {
      const res = await CustomerContactPersonsService.getCustomerContactPersons({
        page: 0,
        size: 10,
        sortDirection: SortDirection.DESC,
        sortField: ICustomerContactPersonSortFields.REMOTE_CUSTOMER_ID,
        registryNumber: form.personalIdentificationCode,
      });
      const match = res?.content?.find(
        (c: ICustomerContactPerson) =>
          c.isPerson && c.personalIdentificationCode === form.personalIdentificationCode
      );
      if (match) {
        setCustomer({
          loading: false,
          loaded: true,
          customerId: match.customerId,
          customerName: match.customerName,
        });
      } else {
        setCustomer({ loading: false, loaded: true });
      }
    } catch {
      setCustomer({ loading: false, loaded: true });
    }
  };

  function onEnterKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (isSearchDisabled) return;
    if (e.keyCode === 13 || e.key === "Enter") {
      e.preventDefault();
      onSearch();
    }
  }

  const isValid = (): boolean => {
    if (!form.name.trim() || !form.dateStart) return false;
    if (!customer.loaded) return false;
    if (customer.customerId) return true;
    return (
      form.personalIdentificationCode.length === PERSONAL_CODE_LENGTH &&
      !!form.firstName.trim() &&
      !!form.lastName.trim() &&
      isEmailValidAndPresent(form.email) &&
      !!form.phone.trim()
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForm(prev => ({ ...prev, validated: true }));
    if (!isValid()) {
      toast.error("Palun täida kõik kohustuslikud väljad");
      return;
    }

    const request: CreateApartmentAgreementRequest = {
      agreementId,
      name: form.name.trim(),
      startDate: form.dateStart,
      endDate: form.dateEnd || undefined,
      ...(customer.customerId
        ? { customerId: customer.customerId }
        : {
            personalIdentificationCode: form.personalIdentificationCode.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
          }),
    };

    setSaving(true);
    try {
      await ElectricityApartmentAgreementsService.create(request);
      toast.success("Korter lisatud");
      onSaved();
    } catch {
      toast.error("Korteri lisamine ebaõnnestus");
    } finally {
      setSaving(false);
    }
  };

  const customerFound = customer.loaded && !!customer.customerId;
  const customerMissing = customer.loaded && !customer.customerId;

  return (
    <Form onSubmit={submit} className="border rounded p-3 mb-3 bg-light" noValidate validated={form.validated}>
      <h5>Lisa korter</h5>

      <div className="row">
        <div className="col-12 col-md-6">
          <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
            <span className="text-nowrap">Korteri nr*</span>
          </Form.Label>
          <Form.Control
            required
            type="text"
            value={form.name}
            maxLength={64}
            onChange={(e) => update("name", e.target.value)}
          />
          <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 col-md-6">
          <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
            <span className="text-nowrap">Algus*</span>
          </Form.Label>
          <DatePicker
            className={`d-block ${!form.dateStart && form.validated ? ' invalid is-invalid' : ''}`}
            value={isoToDate(form.dateStart)}
            locale='et'
            clearIcon={null}
            calendarIcon={<CalendarIcon />}
            format='dd.MM.yyyy'
            minDate={isoToDate(defaultDateStart)}
            maxDate={isoToDate(parentDateEnd)}
            onChange={(e: any) => update("dateStart", dateToIso(e))}
          />
          <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
        </div>
        <div className="col-12 col-md-6 mt-3 mt-lg-0">
          <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
            <span className="text-nowrap">Lõpp</span>
          </Form.Label>
          <DatePicker
            className="d-block"
            value={isoToDate(form.dateEnd)}
            locale='et'
            clearIcon={parentDateEnd ? null : <ClearIcon />}
            calendarIcon={<CalendarIcon />}
            format='dd.MM.yyyy'
            minDate={isoToDate(form.dateStart || defaultDateStart)}
            maxDate={isoToDate(parentDateEnd)}
            onChange={(e: any) => update("dateEnd", dateToIso(e))}
          />
        </div>
      </div>

      <div className="row mt-3" onKeyDown={onEnterKey}>
        <div className="col-12 col-md-6">
          <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
            <span className="text-nowrap">Isiku / registrikood</span>
          </Form.Label>
          <div className="input-group">
            <Form.Control
              value={form.personalIdentificationCode}
              disabled={false}
              type="number"
              onChange={(e) => update("personalIdentificationCode", e.target.value)}
              onWheel={(e: any) => e.target.blur()}
            />
            <div className="input-group-append">
              <button
                className="btn btn-primary rounded d-flex align-items-center"
                type="button"
                disabled={isSearchDisabled}
                onClick={() => {
                  if (!isSearchDisabled) onSearch();
                }}
              >
                {customer.loading ? <Spinner size="sm" animation="border" /> : <i className="icon icon__search d-inline-block"></i>}
              </button>
            </div>
          </div>
          {customerMissing ? <div className="text-danger">{getCustomerNotFoundMessage(false)}</div> : null}
        </div>
      </div>
      {customer.loaded ? (
        <>
          <div className="row mt-3">
            {customerFound ? (
              <div className="col-12 col-md-6">
                <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                  <span className="text-nowrap">Nimi</span>
                </Form.Label>
                <Form.Control
                  value={customer.customerName}
                  disabled={true}
                  type="text"
                />
              </div>
            ) : (
              <>
                <div className="col-12 col-md-6">
                  <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                    <span className="text-nowrap">Eesnimi*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    value={form.firstName}
                    type="text"
                    onChange={(e) => update("firstName", e.target.value)}
                  />
                  <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
                <div className="col-12 col-md-6 mt-3 mt-lg-0">
                  <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                    <span className="text-nowrap">Perenimi*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    value={form.lastName}
                    type="text"
                    onChange={(e) => update("lastName", e.target.value)}
                  />
                  <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
              </>
            )}
          </div>
          {customerMissing ? (
            <div className="row mt-3">
              <div className="col-12 col-md-6 mt-3 mt-lg-0">
                <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                  <span className="text-nowrap">Kontakttelefon*</span>
                </Form.Label>
                <Form.Control
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  type="tel"
                />
                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
              </div>
              <div className={"col-12 col-md-6 mt-3 mt-lg-0"}>
                <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                  <span className="text-nowrap">E-post*</span>
                </Form.Label>
                <Form.Control
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  isInvalid={form.validated && !isEmailValidAndPresent(form.email)}
                />
                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="d-flex mt-4">
        <Button type="submit" variant="primary" disabled={saving || !isValid()} className="mr-2">
          {saving ? <Spinner size="sm" animation="border" /> : "Salvesta"}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Tühista
        </Button>
      </div>
    </Form>
  );
}
